import type { BrandConfig } from '@bv/api/brand';
import { MAINTENANCE_END_MS, MAINTENANCE_START_MS } from '@bv/constants/maintenance/dates';
import { HEALTH_CHECK_ROUTE_NAME, HEALTH_CHECK_ROUTE_PATH } from '@bv/constants/maintenance/health-check';
import {
  MAINTENANCE_API_BLOCK_STORAGE_KEY,
  MAINTENANCE_READY_STORAGE_KEY,
  MAINTENANCE_RETURN_PATH_KEY,
  MAINTENANCE_ROUTE_NAME,
  MAINTENANCE_ROUTE_PATH,
} from '@bv/constants/maintenance/page';

/** `setTimeout` delays above this fire immediately. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/** How often an open tab re-reads `maintenanceMode`. */
const MAINTENANCE_POLL_INTERVAL_MS = 2 * 60 * 1000;

/** Storybook and other hosts that pass `brand: false` skip the domain-brand read. */
let syncMaintenanceMode = true;

/** One shared read for the first navigation of this page load. */
let maintenanceSample: Promise<void> | null = null;

/**
 * A route the maintenance guard can classify without a full router location.
 */
export type MaintenanceRoute = {
  name?: string | symbol | null;
  path: string;
  fullPath: string;
};

/** Where navigation should go while the maintenance rules are in force. */
export type MaintenanceNavigation = 'allow' | 'block' | 'restore';

type MaintenanceRouter = {
  currentRoute: { value: MaintenanceRoute };
  replace: (to: { name: string }) => unknown;
  isReady?: () => Promise<void>;
};

/**
 * Whether `now` is inside the maintenance window, including 1:30 AM and excluding 7:30 AM.
 * @param now - Timestamp in milliseconds. Defaults to the current time.
 */
export const isMaintenanceWindowActive = (now: number = Date.now()): boolean =>
  now >= MAINTENANCE_START_MS && now < MAINTENANCE_END_MS;

/**
 * Whether `domain-brand` says the backend is out of maintenance.
 * Only a strict `false` counts.
 * @param config - Brand payload, or nothing when the call failed.
 */
export const isDomainBrandReady = (config?: Pick<BrandConfig, 'maintenanceMode'> | null): boolean =>
  config?.maintenanceMode === false;

/**
 * Turns the domain-brand read on or off before the router starts navigating.
 * @param enabled - `false` for hosts that do not talk to domain-brand.
 */
export const setMaintenanceModeSyncEnabled = (enabled: boolean): void => {
  syncMaintenanceMode = enabled;
};

/**
 * Whether this tab has already seen `maintenanceMode: false`.
 */
export const isBackendMarkedReady = (): boolean => {
  try {
    return sessionStorage.getItem(MAINTENANCE_READY_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Records that `domain-brand` returned `maintenanceMode: false`.
 * Survives a refresh in this tab and is ignored once 7:30 AM has passed.
 */
export const markBackendReady = (): void => {
  try {
    sessionStorage.setItem(MAINTENANCE_READY_STORAGE_KEY, '1');
  } catch {
    // Private mode can reject storage. The next poll will try again.
  }
};

/**
 * Whether this tab's latest `domain-brand` response said `maintenanceMode: true`.
 * Blocks at any time of day until a later response says `false`.
 */
export const isApiMaintenanceBlock = (): boolean => {
  try {
    return sessionStorage.getItem(MAINTENANCE_API_BLOCK_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Stores the latest `maintenanceMode` for this tab.
 * `true` blocks immediately. `false` lifts that block and records that the backend is ready.
 * Any other payload leaves both flags unchanged.
 * @param config - Brand payload, or nothing when the call failed.
 */
export const applyDomainBrandMaintenance = (config?: Pick<BrandConfig, 'maintenanceMode'> | null): void => {
  if (config?.maintenanceMode === true) {
    try {
      sessionStorage.setItem(MAINTENANCE_API_BLOCK_STORAGE_KEY, '1');
      sessionStorage.removeItem(MAINTENANCE_READY_STORAGE_KEY);
    } catch {
      // Private mode can reject storage. The next poll will try again.
    }
    return;
  }

  if (!isDomainBrandReady(config)) {
    return;
  }

  try {
    sessionStorage.removeItem(MAINTENANCE_API_BLOCK_STORAGE_KEY);
  } catch {
    // The next poll will try again.
  }

  markBackendReady();
};

/**
 * Whether the blocking screen should be showing at `now`.
 * An explicit `maintenanceMode: true` blocks outside the clock window too.
 * @param now - Timestamp in milliseconds. Defaults to the current time.
 */
export const shouldBlockForMaintenance = (now: number = Date.now()): boolean =>
  isApiMaintenanceBlock() || (isMaintenanceWindowActive(now) && !isBackendMarkedReady());

const isMaintenanceRoute = (route: MaintenanceRoute): boolean =>
  route.name === MAINTENANCE_ROUTE_NAME || route.path === MAINTENANCE_ROUTE_PATH;

const isHealthCheckRoute = (route: MaintenanceRoute): boolean =>
  route.name === HEALTH_CHECK_ROUTE_NAME || route.path === HEALTH_CHECK_ROUTE_PATH;

/**
 * Classifies a navigation during and around the maintenance window.
 * `restore` means leave the maintenance screen and reload the app.
 * @param route - Destination route.
 * @param now - Timestamp in milliseconds. Defaults to the current time.
 */
export const maintenanceAccessDecision = (route: MaintenanceRoute, now: number = Date.now()): MaintenanceNavigation => {
  if (isHealthCheckRoute(route)) {
    return 'allow';
  }

  const blocking = shouldBlockForMaintenance(now);

  if (isMaintenanceRoute(route)) {
    return blocking ? 'allow' : 'restore';
  }

  return blocking ? 'block' : 'allow';
};

const isSafeReturnPath = (fullPath: string): boolean =>
  fullPath.startsWith('/') &&
  !fullPath.startsWith('//') &&
  fullPath !== MAINTENANCE_ROUTE_PATH &&
  !fullPath.startsWith(`${MAINTENANCE_ROUTE_PATH}?`) &&
  !fullPath.startsWith(`${MAINTENANCE_ROUTE_PATH}#`);

/**
 * Remembers the page the user was opening so a later reload can return to it.
 * @param fullPath - Router full path, including query and hash.
 */
export const rememberMaintenanceReturnPath = (fullPath: string): void => {
  if (!isSafeReturnPath(fullPath)) {
    return;
  }

  try {
    sessionStorage.setItem(MAINTENANCE_RETURN_PATH_KEY, fullPath);
  } catch {
    // The reload falls back to `/` when the path was not stored.
  }
};

/**
 * Reloads into the app so bootstrap runs again. Uses `/` when no return path was stored.
 */
export const restoreFromMaintenance = (): void => {
  let stored: string | null = null;

  try {
    stored = sessionStorage.getItem(MAINTENANCE_RETURN_PATH_KEY);
    sessionStorage.removeItem(MAINTENANCE_RETURN_PATH_KEY);
  } catch {
    stored = null;
  }

  const path = stored && isSafeReturnPath(stored) ? stored : '/';
  window.location.assign(path);
};

/**
 * Reads `maintenanceMode` once and stores it. A failed call leaves the flags unchanged.
 */
const sampleDomainBrand = async (): Promise<void> => {
  try {
    const { brandApi } = await import('@bv/api/brand');
    const config = await brandApi.config({ headers: { 'x-silent': 'true' } });
    applyDomainBrandMaintenance(config);
  } catch {
    // Clock rules still apply. The next poll will try again.
  }
};

/**
 * Shares the first `domain-brand` read across the guard and the open-tab sync.
 */
const ensureMaintenanceSampled = (): Promise<void> => {
  if (!maintenanceSample) {
    maintenanceSample = sampleDomainBrand();
  }

  return maintenanceSample;
};

/**
 * Waits for the first `domain-brand` read of this page load when sync is enabled.
 * Hosts that disable brand setup resolve immediately.
 */
export const syncMaintenanceModeFromBrand = (): Promise<void> => {
  if (!syncMaintenanceMode) {
    return Promise.resolve();
  }

  return ensureMaintenanceSampled();
};

const applyMaintenanceRoute = (router: MaintenanceRouter, route: MaintenanceRoute): void => {
  const decision = maintenanceAccessDecision(route);

  if (decision === 'block') {
    rememberMaintenanceReturnPath(route.fullPath);
    void router.replace({ name: MAINTENANCE_ROUTE_NAME });
    return;
  }

  if (decision === 'restore') {
    restoreFromMaintenance();
  }
};

/**
 * Applies the maintenance decision for one navigation.
 * The first call of a page load waits for `domain-brand`, except on the health check.
 * `null` means the caller should continue with its own guard.
 * @param route - Destination route.
 */
export const settleMaintenanceNavigation = async (
  route: MaintenanceRoute,
): Promise<{ name: typeof MAINTENANCE_ROUTE_NAME } | 'reload' | null> => {
  if (syncMaintenanceMode && !isHealthCheckRoute(route)) {
    await ensureMaintenanceSampled();
  }

  const decision = maintenanceAccessDecision(route);

  if (decision === 'block') {
    rememberMaintenanceReturnPath(route.fullPath);
    return { name: MAINTENANCE_ROUTE_NAME };
  }

  if (decision === 'restore') {
    return 'reload';
  }

  return null;
};

/**
 * Re-reads `maintenanceMode` while this tab is open and moves to or from the maintenance screen.
 * The first read is the same one the navigation guard waits for.
 * @param router - Router that owns the maintenance route.
 */
export const startMaintenanceModeSync = (router: MaintenanceRouter): void => {
  if (!syncMaintenanceMode) {
    return;
  }

  let polling = false;

  const pollDomainBrand = async (): Promise<void> => {
    if (polling || document.visibilityState !== 'visible') {
      return;
    }

    polling = true;

    try {
      await sampleDomainBrand();
      applyMaintenanceRoute(router, router.currentRoute.value);
    } finally {
      polling = false;
    }
  };

  const begin = (): void => {
    void ensureMaintenanceSampled().then(() => {
      applyMaintenanceRoute(router, router.currentRoute.value);
    });

    window.setInterval(() => {
      void pollDomainBrand();
    }, MAINTENANCE_POLL_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void pollDomainBrand();
      }
    });
  };

  if (router.isReady) {
    void router.isReady().then(begin);
    return;
  }

  begin();
};

const scheduleAt = (at: number, task: () => void): void => {
  const delay = at - Date.now();

  if (delay <= 0) {
    return;
  }

  window.setTimeout(
    () => {
      if (Date.now() + 1000 < at) {
        scheduleAt(at, task);
        return;
      }

      task();
    },
    Math.min(delay, MAX_TIMEOUT_MS),
  );
};

/**
 * Moves an already-open tab onto the maintenance screen at 1:30 AM,
 * and reloads out of it at 7:30 AM unless `maintenanceMode` is still `true`.
 * @param router - Router that owns the maintenance route.
 */
export const scheduleMaintenanceTransitions = (router: MaintenanceRouter): void => {
  scheduleAt(MAINTENANCE_START_MS, () => {
    const current = router.currentRoute.value;

    if (!shouldBlockForMaintenance() || isMaintenanceRoute(current)) {
      return;
    }

    rememberMaintenanceReturnPath(current.fullPath);
    void router.replace({ name: MAINTENANCE_ROUTE_NAME });
  });

  scheduleAt(MAINTENANCE_END_MS, () => {
    if (isApiMaintenanceBlock()) {
      return;
    }

    if (isMaintenanceRoute(router.currentRoute.value)) {
      restoreFromMaintenance();
    }
  });
};
